/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import {
  searchEmbeddedContent,
  processEmbeddingResults,
  createChunkStorageAtom,
  type EmbeddingSearchResult,
} from "../../shared/embedding-utils";
import { recipes } from "./recipes/index";

interface RecipeSearchResult extends EmbeddingSearchResult {
  source: string;
}

export const SearchRecipesToolParams = z.object({
  query: z
    .string()
    .describe(
      "The search query to find similar implementation recipes and code patterns",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Finding implementation guide...' or 'Loading recipe...'",
    ),
});

export type SearchRecipesToolParamsType = z.infer<
  typeof SearchRecipesToolParams
>;

async function searchSimilarRecipes(
  query: string,
): Promise<RecipeSearchResult[]> {
  return searchEmbeddedContent({
    endpoint: "/api/canvasui/search-recipes",
    query,
    maxResults: 3,
  }) as Promise<RecipeSearchResult[]>;
}

export const searchRecipesToolDescription = `Searches for implementation recipes and code patterns that can help achieve the user's goal.

MANDATORY: Use category-specific searches before API calls:
- Agent tasks → Search with: "agent recipes", "ai agent setup", "agent configuration"  
- Table tasks → Search with: "table recipes", "database setup", "table configuration"
- Interface tasks → Search with: "interface recipes", "form setup", "interface configuration"
- Zap tasks → Search with: "zap recipes", "automation setup", "workflow configuration"
- Field mapping tasks → Search with: "field mapping", "configure parameters", "map fields"

- query: The search query to find similar implementation recipes and code patterns - use for specific technical implementations and category-specific searches.
- If you want to discover phase, use the search recipes tool to find specific information that could help with the API calls or execution flow of that step. If an item is returned, prioritize the instructions in that recipe.
- Always perform category-specific recipe searches BEFORE making API calls in that category to ensure you have the proper implementation guidance.`;

export const recipeChunksAtom =
  createChunkStorageAtom<RecipeSearchResult>("recipeChunksAtom");

export const searchRecipesToolAtom = atom<
  JotaiTool<z.infer<typeof SearchRecipesToolParams>>
>(() => ({
  name: "search_recipes_tool",
  description: searchRecipesToolDescription,
  parameters: SearchRecipesToolParams,
  execute: atom(null, async (_get, set, { args: rawArgs }) => {
    let params: SearchRecipesToolParamsType;
    try {
      params = SearchRecipesToolParams.parse(rawArgs || {});
    } catch (error) {
      console.error(
        "[SEARCH_RECIPES_TOOL] Error parsing arguments for searchRecipesTool:",
        error,
      );
      return {
        content: [
          {
            type: "text",
            text: `Error: Invalid arguments provided for search_recipes_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
          },
        ],
      };
    }

    const { query } = params;

    try {
      const results = await searchSimilarRecipes(query);
      const { chunkMap, summaryResults } = processEmbeddingResults(results);

      set(recipeChunksAtom, chunkMap);

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(summaryResults, null, 2),
          },
        ],
      };
    } catch (error) {
      console.error(
        "[SEARCH_RECIPES_TOOL] Error in searchRecipesToolExecute:",
        error,
      );
      return {
        content: [
          {
            type: "text",
            text: `Error searching similar recipes: ${error instanceof Error ? error.message : String(error)}`,
          },
        ],
      };
    }
  }),
}));

searchRecipesToolAtom.debugLabel = "searchRecipesToolAtom";

export const activeRecipeAtom = atom<string | null>(null);
activeRecipeAtom.debugLabel = "activeRecipeAtom";

// Use Recipe Tool Parameters
export const UseRecipeToolParams = z.object({
  recipeName: z.string().describe("The name of the recipe to activate"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Activating recipe...' or 'Loading tools...'",
    ),
});

export type UseRecipeToolParamsType = z.infer<typeof UseRecipeToolParams>;

export const useRecipeToolDescription = `Activates a specific implementation recipe after you have searched for and found it relevant.
This will load the recipe's instructions and specialized tools to help with the specific task.
- recipeName: The exact name of the recipe to activate 
Only call this tool after you have used search_recipes_tool and determined a recipe is relevant to the user's request.`;

export const useRecipeToolAtom = atom<
  JotaiTool<z.infer<typeof UseRecipeToolParams>>
>(() => ({
  name: "use_recipe",
  description: useRecipeToolDescription,
  parameters: UseRecipeToolParams,
  execute: atom(null, async (_get, set, { args: rawArgs }) => {
    let params: UseRecipeToolParamsType;
    try {
      params = UseRecipeToolParams.parse(rawArgs || {});
    } catch (error) {
      console.error(
        "[USE_RECIPE_TOOL] Error parsing arguments for useRecipeTool:",
        error,
      );
      return {
        content: [
          {
            type: "text",
            text: `Error: Invalid arguments provided for use_recipe. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
          },
        ],
      };
    }
    const { recipeName } = params;
    const recipe = recipes.find((recipe) => recipe.id === recipeName);
    if (!recipe) {
      return {
        content: [
          {
            type: "text",
            text: `Error: Recipe '${recipeName}' not found. Available recipes: ${Object.keys(recipes).join(", ")}`,
          },
        ],
      };
    }

    // Set the active recipe which will trigger the tools to be loaded
    set(activeRecipeAtom, recipeName);
    return {
      content: [
        {
          type: "text",
          text: `Recipe '${recipeName}' activated successfully. Specialized tools and instructions are now available.`,
        },
      ],
    };
  }),
}));

useRecipeToolAtom.debugLabel = "useRecipeToolAtom";

/* eslint-enable sort-exports/sort-exports */
